<template>
  <ClientOnly>
    <SharedIconButton
      v-if="mounted"
      :variant="isFavorite ? 'accent-inverted' : 'accent'"
      :size="size || 'small'"
      :aria-label="isFavorite ? t('components.featured-cars.favorited') : t('components.featured-cars.favorite')"
      :title="isFavorite ? t('components.featured-cars.favorited') : t('components.featured-cars.favorite')"
      @click="favoriteCarToggled(carId, carDetail)"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M12.0005 4.59499C10.9109 3.59309 9.48468 3.03703 8.00447 3.03699C7.22101 3.0378 6.44544 3.19355 5.72241 3.49526C4.99937 3.79696 4.34313 4.23868 3.79147 4.79499C1.43847 7.15799 1.43947 10.854 3.79347 13.207L11.1255 20.539C11.2955 20.838 11.6235 21.031 12.0005 21.031C12.1553 21.0295 12.3076 20.9917 12.4451 20.9207C12.5827 20.8496 12.7016 20.7473 12.7925 20.622L20.2075 13.207C22.5615 10.853 22.5615 7.15799 20.2055 4.79099C19.6541 4.2357 18.9984 3.79488 18.276 3.49387C17.5537 3.19286 16.779 3.03759 15.9965 3.03699C14.5163 3.03723 13.0902 3.59326 12.0005 4.59499ZM18.7915 6.20499C20.3545 7.77599 20.3555 10.23 18.7935 11.793L12.0005 18.586L5.20747 11.793C3.64547 10.23 3.64647 7.77599 5.20547 6.20899C5.96547 5.45299 6.95947 5.03699 8.00447 5.03699C9.04947 5.03699 10.0395 5.45299 10.7935 6.20699L11.2935 6.70699C11.3863 6.79993 11.4965 6.87367 11.6178 6.92398C11.7391 6.97429 11.8691 7.00019 12.0005 7.00019C12.1318 7.00019 12.2618 6.97429 12.3832 6.92398C12.5045 6.87367 12.6147 6.79993 12.7075 6.70699L13.2075 6.20699C14.7195 4.69799 17.2815 4.70199 18.7915 6.20499Z"
          fill="currentColor"
        />
      </svg>
    </SharedIconButton>
  </ClientOnly>
</template>

<script setup lang="ts">
  import { useStorage } from '@vueuse/core';

  import type { Toast } from '~/components/GlobalToast/index.vue';
  import type { GraphQLCarDetail } from '~/types/graphql';
  interface EventData {
    event: 'add_to_favorites' | 'remove_from_favorites';
    page_location: string;
    page_title: string;
    vehicle_id: string;
    make?: string;
    model?: string;
    manufactured_at?: string;
    price?: number;
    fuel_type?: string;
  }

  const { t } = useI18n();

  const props = defineProps<{
    carId: string;
    carDetail?: GraphQLCarDetail;
    size?: 'small' | 'medium' | 'large';
  }>();

  const { getLocaleFavoritesLink } = useCustomLocaleRoute();
  const router = useRouter();

  const mounted = ref(false);

  onMounted(() => {
    mounted.value = true;
  });

  const favoriteCars = useStorage<string[]>('favoriteCars', []);
  const eventBus = useEventBus();

  const { push } = useDataLayer();

  const favoriteCarToggled = (carId: string, carDetail?: GraphQLCarDetail) => {
    // TODO: this might move to the Cockpit at some point
    if (favoriteCars.value.includes(carId)) {
      favoriteCars.value = favoriteCars.value.filter(id => id !== carId);

      const eventData: EventData = {
        event: 'remove_from_favorites',
        page_location: window.location.href,
        page_title: window.document.title,
        vehicle_id: props.carDetail ? props.carDetail.internal_vehicle_no?.toString() || '' : carId,
      };

      if (carDetail) {
        eventData.make = carDetail.make?.name;
        eventData.model = carDetail.model;
        eventData.manufactured_at = carDetail.year_of_manufacture?.toString() || undefined;
        eventData.price = carDetail.asking_price;
        eventData.fuel_type = carDetail.fuel_type;
      }

      push(eventData);

      const toast: Toast = {
        show: true,
        message: t('components.favoriteCarButton.removedFromFavorites'),
        type: 'foreground',
        onClickAction: () => {
          router.push(getLocaleFavoritesLink());
        },
      };

      eventBus.emit('add-global-toast', toast);
    } else {
      favoriteCars.value.push(carId);
      
      const eventData: EventData = {
        event: 'add_to_favorites',
        page_location: window.location.href,
        page_title: window.document.title,
        vehicle_id: props.carDetail ? props.carDetail.internal_vehicle_no?.toString() || '' : carId,
      };
      
      if (carDetail) {
        eventData.make = carDetail.make?.name;
        eventData.model = carDetail.model;
        eventData.manufactured_at = carDetail.year_of_manufacture?.toString() || undefined;
        eventData.price = carDetail.asking_price;
        eventData.fuel_type = carDetail.fuel_type;
      }
      
      push(eventData);

      const toast: Toast = {
        show: true,
        message: t('components.favoriteCarButton.addedToFavorites'),
        type: 'foreground',
        onClickAction: () => {
          router.push(getLocaleFavoritesLink());
        },
      };

      eventBus.emit('add-global-toast', toast);
    }
  };

  const isFavorite = computed(() => favoriteCars.value.includes(props.carId));
</script>

<style lang="scss" scoped></style>
